import React from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';

// ─── Tunable parameters ────────────────────────────────────────────────────────

export const LOGO_CFG = {
  hoverScale:      1.055,   // scale multiplier on hover
  springStiffness: 420,     // Framer Motion spring stiffness
  springDamping:   22,      // Framer Motion spring damping
  shimmerDuration: '560ms', // CSS background-position sweep speed
  glowRadius:      '14px',  // drop-shadow blur radius on hover
  glowOpacity:     0.55,    // glow alpha (0–1)
};

// ─── Size map ─────────────────────────────────────────────────────────────────

const SIZES = {
  sm: '17px',
  md: '22px',
  lg: '26px',
} as const;

// ─── Styled pieces ────────────────────────────────────────────────────────────

// The gradient is school-tinted metallic:
//   school-primary (dark edge) → steel grey → silver white highlight → steel grey → school-primary (dark edge)
// The school color anchors both ends; the bright silver sweeps through the middle on hover.
// background-size: 300% lets the highlight travel across the letters.

const LogoText = styled.span<{ $primary: string; $fontSize: string }>`
  display: inline-block;
  font-family: 'Cormorant Garamond', serif;
  font-weight: 600;
  font-style: italic;
  font-size: ${p => p.$fontSize};
  letter-spacing: 0.02em;
  line-height: 1;
  background: linear-gradient(
    108deg,
    ${p => p.$primary}  0%,
    #a8a8bc             20%,
    #dcdce8             36%,
    #f4f4fa             48%,
    #dcdce8             62%,
    #a8a8bc             78%,
    ${p => p.$primary}  100%
  );
  background-size: 300% 100%;
  background-position: 0% center;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 0 0px transparent);
  transition:
    background-position ${LOGO_CFG.shimmerDuration} ease,
    filter 300ms ease;
`;

// Wrapper passes $primary so the hover glow uses the school color
const LogoWrap = styled(motion.div)<{ $primary: string }>`
  display: inline-flex;
  align-items: center;

  &:hover ${LogoText} {
    background-position: 100% center;
    filter: drop-shadow(
      0 0 ${LOGO_CFG.glowRadius}
      ${p => p.$primary}${Math.round(LOGO_CFG.glowOpacity * 255).toString(16).padStart(2, '0')}
    );
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  /** React Router path — renders as a <Link>. Omit for non-interactive use. */
  to?: string;
  /** School-theme primary color. Drives the metallic gradient tint and glow. */
  primary?: string;
  /** Visual size preset. */
  size?: keyof typeof SIZES;
  style?: React.CSSProperties;
  className?: string;
}

// Default: neutral steel when no school is selected
const DEFAULT_PRIMARY = '#6e6e82';

export function IncubsLogo({
  to,
  primary = DEFAULT_PRIMARY,
  size = 'md',
  style,
  className,
}: Props) {
  const text = (
    <LogoText $primary={primary} $fontSize={SIZES[size]}>
      Incubs
    </LogoText>
  );

  return (
    <LogoWrap
      $primary={primary}
      style={style}
      className={className}
      whileHover={{ scale: LOGO_CFG.hoverScale }}
      transition={{
        type: 'spring',
        stiffness: LOGO_CFG.springStiffness,
        damping: LOGO_CFG.springDamping,
      }}
    >
      {to ? (
        <Link to={to} style={{ textDecoration: 'none' }}>
          {text}
        </Link>
      ) : (
        text
      )}
    </LogoWrap>
  );
}
